/**
 * The sample analysis drawn on the homepage.
 *
 * Every figure and finding here is what the real engine returns for the
 * built-in fictional sample resume (`sampleResume()` in ./defaults.ts). It is
 * restated as plain data rather than computed on the page because the engine
 * would pull the whole analysis bundle into the homepage's initial JS — the
 * same trade ./homeIndex.ts makes for the guide cards.
 *
 * homeSample.test.ts runs the engine and fails if any value here stops
 * matching, so the homepage cannot show a score or a suggestion the product
 * would not actually produce.
 */

/** The fictional person in `sampleResume()`. */
export const SAMPLE_PERSON = {
  name: 'Ananya Sharma',
  headline: 'Frontend Engineer',
  location: 'Bengaluru, India',
  role: 'Frontend Engineer',
  company: 'Meridian Health',
  dates: 'Mar 2022 – Present',
  bullets: [
    'Rebuilt the patient booking flow in React and TypeScript, increasing completed bookings by 23% within one quarter.',
    'Worked on the design system used by four product teams.',
    'Led accessibility remediation across 40 screens, taking the product from 61 to 98 on automated audits and closing a compliance blocker.',
  ],
  /** Index into `bullets` of the line the checker flags. */
  flagged: 1,
}

/** `analyzeAts(sampleResume())` */
export const SAMPLE_ATS = {
  score: 92,
  /** A subset of the six categories, in the engine's order. */
  categories: [
    { label: 'Contact & Identity', score: 15, max: 15 },
    { label: 'ATS-Safe Formatting', score: 20, max: 20 },
    { label: 'Content Quality', score: 17, max: 25 },
    { label: 'Keywords & Skills', score: 10, max: 10 },
  ],
  /** Titles of the engine's priority fixes, first three. */
  findings: ['4 of 11 bullets start with weak phrasing', 'Weak phrasing: "worked on"', 'Weak phrasing: "responsible for"'],
}

/**
 * What the ATS score is made of: the six categories `analyzeAts` returns, in
 * its order, with their maximum points (they sum to 100), and the number of
 * rule checks behind them (`RULE_CHECK_COUNT` in engine/atsHealth.ts).
 */
export const SCORE_CATEGORIES = [
  { label: 'Contact & Identity', max: 15, body: 'Your name, email, phone, location and LinkedIn profile.' },
  { label: 'Sections & Structure', max: 20, body: 'A summary, experience or projects, skills, education, and no empty headings.' },
  { label: 'ATS-Safe Formatting', max: 20, body: 'One date format, standard fonts and sizes, no emoji, icons, tabs or decorative symbols.' },
  { label: 'Content Quality', max: 25, body: 'Opening verbs, measurable results, first-person pronouns, and bullet and summary length.' },
  { label: 'Keywords & Skills', max: 10, body: 'Enough named skills, grouped by category, and your target job title.' },
  { label: 'Length & Density', max: 10, body: 'Page count for your experience level, and how many bullets each role carries.' },
]
export const RULE_CHECKS = 56

/** `analyzeBullet(SAMPLE_BULLET.original)` */
export const SAMPLE_BULLET = {
  original: 'Worked on the design system used by four product teams.',
  score: 54,
  issues: [
    '"worked on" weakens this bullet',
    'No number anywhere in this bullet',
    'States an activity but not an outcome',
  ],
  why: 'The single most common weak opener. Says nothing about your contribution.',
  suggestion: 'Developed the design system used by four product teams.',
  /** One of the metric templates the engine offers. The blanks are yours to fill. */
  metricTemplate: ', improving task completion rate from __% to __%',
}
